import { cn, focusRing } from "@simpro/ui";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { useSalonMoney } from "@/features/salon";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { cheapest } from "../lib/cheapest";
import { formatDuration } from "../lib/formatDuration";
import { MAX_SERVICES, type PublicCatalog } from "../schemas/booking.schemas";
import { AddServiceButton } from "./AddServiceButton";
import { type Plan, PlanCards } from "./PlanCards";

export interface PriceCardsProps {
  catalog: PublicCatalog;
  /** In the order they were added, which is the order they are done in. */
  selected: readonly string[];
  onToggle: (serviceId: string) => void;
  /** The salon's own heading (FR-SA-191); "Prices" when it wrote none. */
  heading?: string | null;
  /** The salon's plans, shown in place of the menu when it chose plans (FR-SA-191). */
  plans?: readonly Plan[];
}

/** How many of a heading's services its card lists; the rest are a tap away on the menu. */
const SHOWN = 4;

/**
 * The price list (design note salon-home-landing.md section 5, item 6; FR-SA-190, FR-SA-138): one card
 * per menu heading, two across from a tablet. Each lists its first services with their prices and a
 * button that adds them to the visit, which the basket bar carries into booking. A salon that chose
 * plans gets its plans instead (FR-SA-191); there is nothing to sell online (TDD-SA D6).
 *
 * Drawn as a menu card (design note salon-booking-premium-redesign.md section 5.3 item 8): the
 * heading in the display face with its lowest price in the accent, the services on hairlines with
 * tabular prices, and a quiet link to the rest of the heading.
 */
export function PriceCards({ catalog, selected, onToggle, heading, plans = [] }: PriceCardsProps) {
  const money = useSalonMoney();
  const categories = catalog.filter((category) => category.services.length > 0);
  if (categories.length === 0 && plans.length === 0) return null;
  const full = selected.length >= MAX_SERVICES;

  return (
    <section aria-labelledby="prices-heading" className={cn(page, "flex flex-col gap-10 lg:gap-14")}>
      <SectionHeading
        id="prices-heading"
        eyebrow={plans.length > 0 ? "Plans" : "Price list"}
        title={heading ?? "Prices"}
        centred
      />
      {plans.length > 0 ? (
        <PlanCards plans={plans} />
      ) : (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6">
          {categories.map((category, index) => {
            const from = cheapest(category.services);
            return (
              <Reveal
                as="li"
                key={category.id}
                index={index}
                className="flex flex-col gap-6 rounded-card border border-edge-subtle bg-surface p-6 lg:p-8"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="headline text-[1.625rem] leading-tight text-heading lg:text-[2rem]">
                    {category.name}
                  </h3>
                  {from ? (
                    <p className="shrink-0 text-sm text-subtle">
                      from <span className="font-semibold text-accent-ink tabular-nums">{money(from)}</span>
                    </p>
                  ) : null}
                </div>
                <ul className="flex flex-col divide-y divide-edge-subtle border-y border-edge-subtle">
                  {category.services.slice(0, SHOWN).map((service) => {
                    const added = selected.includes(service.id);
                    return (
                      <li key={service.id} className="flex items-center gap-4 py-3.5">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-heading">{service.name}</span>
                          <span className="text-[13px] text-muted">{formatDuration(service.durationMinutes)}</span>
                        </span>
                        <span className="font-semibold text-heading tabular-nums">{money(service.price)}</span>
                        <AddServiceButton
                          name={service.name}
                          added={added}
                          disabled={full}
                          onToggle={() => onToggle(service.id)}
                        />
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to={`/services#menu-${encodeURIComponent(category.id)}`}
                  viewTransition
                  className={cn(
                    "group/link mt-auto inline-flex items-center gap-1.5 self-start rounded-sm text-sm font-semibold text-heading",
                    focusRing,
                  )}
                >
                  <span className="link-draw">All {category.services.length}</span>
                  <span className="sr-only"> {category.name} services</span>
                  <ArrowRight
                    className="size-4 transition-transform duration-300 group-hover/link:translate-x-0.5"
                    aria-hidden
                  />
                </Link>
              </Reveal>
            );
          })}
        </ul>
      )}
    </section>
  );
}
